import React, { Fragment, useState, useContext } from 'react'
import { useMutation } from '@apollo/client'
import { Loader } from 'semantic-ui-react'
import { UPDATE_JOB_MUTATION } from '../../../graphql/job'
import { getInvitationEmail } from '../../Emails'
import EmailModal from './EmailModal'
import Alert from '../../Alert'
import { SingleProjectContext } from '../../../Context'
import DataContext from '../../../contexts/DataContext'
import { AuthContext } from '@dexter-experts/auth-react'

const InviteModal = ({
    onShow,
    onHide,
    selectedExperts,
    setSelectedExperts,
    project,
    onSave,
}) => {
    const { authUser } = useContext(AuthContext)
    const { user } = useContext(DataContext)
    const singleProjectContext = useContext(SingleProjectContext)

    const [adding, setAdding] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [alertHeader, setAlertHeader] = useState('')
    const [alertBody, setAlertBody] = useState('')

    const [updateJob] = useMutation(UPDATE_JOB_MUTATION)

    const onInvite = (email_content, sendEmail, email_subject) => {
        if (project.team.availableInvites <= 0) {
            setShowAlert(true)
            setAlertHeader('No available invites !')
            setAlertBody('Please purchase more credits')
            return
        }

        setAdding(true)

        let promises = []
        let success = []
        let failed = []
        selectedExperts.forEach((expert) => {
            const existingJob = project.jobs.find(
                (job) => job.user.id === expert.id
            )
            const canInvite =
                expert.expert.status === 'Active' &&
                (!existingJob ||
                    (
                        existingJob.invite_status !== 'Invited' &&
                        existingJob.response_status === 'Pending' &&
                        existingJob.overall_status !== 'Pending Scheduling'
                    ))

            const emailInfo = {
                recipient: {
                    email: process.env.NODE_ENV === 'production' ? expert.email : 'engineering@dexterexperts.com',
                },
                sender: {
                    email: `${authUser.email}`,
                    name: 'Dexter',
                },
                cc: user.isSuperUser()
                    ? []
                    : project.owners.map((owner) => owner.email),
                text: 'New Project Invite',
            }
            if (canInvite) {
                success.push(expert)
                promises.push(
                    updateJob({
                        variables: {
                            userId: expert.id,
                            projectId: project.id,
                            job: {
                                invite_status: 'Invited',
                                response_status: 'Pending',
                                overall_status: 'Pending Scheduling',
                                invitedAt: new Date(),
                                invitedReminderAt: new Date(),
                                email_logs: [
                                    {
                                        subject: email_subject,
                                        sent_at: new Date(),
                                    },
                                ],
                            },
                        },
                        update: (cache, { data: { updateJob } }) => {
                            if (singleProjectContext) {
                                singleProjectContext.setProject(
                                    updateJob.project
                                )
                            }
                            sendEmail(
                                emailInfo.recipient,
                                emailInfo.sender,
                                emailInfo.cc,
                                emailInfo.text,
                                getInvitationEmail(
                                    `${expert.fullName || expert.email}`,
                                    project.title,
                                    email_content,
                                    project.end_date,
                                    user.isSuperUser()
                                        ? updateJob.offerRate
                                        : null
                                )
                            )
                        },
                    })
                )
            } else {
                failed.push(expert)
            }
        })

        Promise.all(promises).then(() => {
            setAdding(false)
            setShowAlert(true)
            setAlertHeader('Email Sent')
            setAlertBody(
                <div>
                    <p>
                        {success.length} emails successfully sent ! <br />
                        {failed.length} failed due to invalid status.
                    </p>
                    {failed.length > 0 ? (
                        <p>
                            Failed:{' '}
                            {failed
                                .map(
                                    (expert) =>
                                        expert.fullName || expert.email
                                )
                                .join(', ')}
                        </p>
                    ) : null}
                </div>
            )
            if (onSave) {
                onSave()
            }
            setSelectedExperts([])
            onHide()
        })
    }

    if (adding) return <Loader active />

    return (
        <Fragment>
            <Alert
                show={showAlert}
                header={alertHeader}
                body={alertBody}
                onClose={() => setShowAlert(false)}
            />
            <EmailModal
                onShow={onShow}
                onHide={onHide}
                project={project}
                type="Invite"
                title={`Invite ${selectedExperts.length} expert(s) to this project ?`}
                buttonText={`Send ${selectedExperts.length} email(s)`}
                onSend={onInvite}
            />
        </Fragment>
    )
}

export default InviteModal
